import { Box, Grid, Paper, Stack } from "@mui/material";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import executiveValueFormatter from "src/scenes/overview/overview-components/executive-value-formatter";
import React from "react";
import styled from "styled-components";
import ReactCardFlip from "react-card-flip";
import MaintainabilityButton from "src/scenes/overview/overview-components/maintainability-button";
import { differenceInDays, parseISO } from "date-fns";
import calculateDependencies from "src/scenes/overview/overview-components/calculate-dependencies";
import AccuracyLinear from "src/scenes/overview/overview-components/accuracy-linear";
import { docsPathConstants, getDocsPath } from "../../../../utils/docs-path-constants";
import ExternalLink from "../../../../components/external-link";

const CustomPaper = styled(Paper)`
    width: 100%;
    padding: 16px;
    border-radius: 0 0 14px 14px;
`;

const MetricHeaderText = styled.p`
    margin: 12px 0 12px 0;
    font-size: 15px;
    font-weight: 600;
`;

const MetricBreakdownText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 300;
`;

const InfoText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 200;
`;

const BreakdownContainer = styled(Box)`
    padding: 12px;
    border-radius: 12px;
    background: #393939;
    margin-top: 12px;
`;

const CodeCost = ({ fetchAction, data, expandedView, isFlipped }) => {
    return (
        <ReactCardFlip isFlipped={isFlipped} containerStyle={{ width: "100%", height: "100%" }}>
            <CustomPaper>
                {!isFlipped && (
                    <>
                        <MetricHeaderText style={{ margin: 0, fontWeight: 200, opacity: 0.5 }}>
                            Code Credit Cost
                        </MetricHeaderText>
                        <Grid container spacing={1}>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Maintainability Index</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.maintainability &&
                                            data.maintainability.meta &&
                                            data.maintainability.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction &&
                                        fetchAction.data &&
                                        fetchAction.data.maintainability
                                    }
                                >
                                    <MaintainabilityButton
                                        codeQualityActivated={
                                            fetchAction &&
                                            fetchAction.data &&
                                            fetchAction.data.maintainability &&
                                            fetchAction.data.maintainability.codeQualityEnabled
                                        }
                                    />
                                    {fetchAction &&
                                        fetchAction.data &&
                                        fetchAction.data.maintainability &&
                                        fetchAction.data.maintainability.codeQualityEnabled &&
                                        executiveValueFormatter(
                                            data &&
                                                data.maintainability &&
                                                data.maintainability.total.value,
                                            "custom",
                                            false,
                                            "Total index change",
                                            data &&
                                                data.maintainability &&
                                                data.maintainability.stats &&
                                                data.maintainability.stats.slope
                                        )}
                                </ShowWithSkeleton>
                                {expandedView &&
                                    fetchAction &&
                                    fetchAction.data &&
                                    fetchAction.data.maintainability &&
                                    fetchAction.data.maintainability.codeQualityEnabled && (
                                        <BreakdownContainer>
                                            <Stack
                                                direction="column"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <MetricBreakdownText>
                                                    Top 5 movers
                                                </MetricBreakdownText>
                                                <ShowWithSkeleton
                                                    fetchAction={fetchAction}
                                                    streamingProperty={
                                                        fetchAction &&
                                                        fetchAction.data &&
                                                        fetchAction.data.maintainability &&
                                                        fetchAction.data.maintainability.breakdown
                                                    }
                                                >
                                                    <Stack direction="column">
                                                        {data &&
                                                            data.maintainability &&
                                                            data.maintainability.breakdown &&
                                                            data.maintainability.breakdown
                                                                .sort(
                                                                    (
                                                                        {
                                                                            maintainability_influence:
                                                                                a,
                                                                        },
                                                                        {
                                                                            maintainability_influence:
                                                                                b,
                                                                        }
                                                                    ) => Math.abs(b) - Math.abs(a)
                                                                )
                                                                .slice(0, 5)
                                                                .map((breakdown) => (
                                                                    <Stack
                                                                        direction="row"
                                                                        justifyContent="space-between"
                                                                    >
                                                                        <MetricBreakdownText>
                                                                            {
                                                                                breakdown.hostname_repo_full_name
                                                                            }
                                                                        </MetricBreakdownText>
                                                                        {executiveValueFormatter(
                                                                            breakdown.maintainability_influence,
                                                                            "custom",
                                                                            true
                                                                        )}
                                                                    </Stack>
                                                                ))}
                                                    </Stack>
                                                </ShowWithSkeleton>
                                            </Stack>
                                        </BreakdownContainer>
                                    )}
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Code Repository Silos</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.codeSilos &&
                                            data.codeSilos.meta &&
                                            data.codeSilos.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction &&
                                        fetchAction.data &&
                                        fetchAction.data.codeSilos
                                    }
                                >
                                    {executiveValueFormatter(
                                        data && data.codeSilos && data.codeSilos.total.value,
                                        "custom",
                                        false,
                                        "Single Contributor Repositories",
                                        data &&
                                            data.codeSilos &&
                                            data.codeSilos.stats &&
                                            data.codeSilos.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="column" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>Top 5 active</MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.codeSilos &&
                                                    fetchAction.data.codeSilos.breakdown
                                                }
                                            >
                                                <Stack direction="column">
                                                    {data &&
                                                        data.codeSilos &&
                                                        data.codeSilos.breakdown
                                                            .sort(
                                                                (
                                                                    {
                                                                        commitTs: a,
                                                                        commits: aCommits,
                                                                    },
                                                                    {
                                                                        commitTs: b,
                                                                        commits: bCommits,
                                                                    }
                                                                ) =>
                                                                    differenceInDays(
                                                                        parseISO(b),
                                                                        parseISO(a)
                                                                    ) || bCommits - aCommits
                                                            )
                                                            .slice(0, 5)
                                                            .map((breakdown) => (
                                                                <Stack
                                                                    direction="row"
                                                                    justifyContent="space-between"
                                                                >
                                                                    <MetricBreakdownText>
                                                                        {breakdown.repoName}
                                                                    </MetricBreakdownText>{" "}
                                                                    {executiveValueFormatter(
                                                                        breakdown.singlePersonRatio,
                                                                        "custom",
                                                                        true
                                                                    )}
                                                                </Stack>
                                                            ))}
                                                </Stack>
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Backlog Size</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.backlog &&
                                            data.backlog.meta &&
                                            data.backlog.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction && fetchAction.data && fetchAction.data.backlog
                                    }
                                >
                                    {executiveValueFormatter(
                                        data && data.backlog && data.backlog.total.value,
                                        "custom",
                                        false,
                                        "total issues",
                                        data &&
                                            data.backlog &&
                                            data.backlog.stats &&
                                            data.backlog.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Production backlog issues:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.backlog &&
                                                    fetchAction.data.backlog.breakdown &&
                                                    fetchAction.data.backlog.breakdown.prodIssues
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.backlog &&
                                                        data.backlog.breakdown.prodIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Issues created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.backlog &&
                                                    fetchAction.data.backlog.breakdown &&
                                                    fetchAction.data.backlog.breakdown.newIssues
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.backlog &&
                                                        data.backlog.breakdown.newIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Prod Issues created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.backlog &&
                                                    fetchAction.data.backlog.breakdown &&
                                                    fetchAction.data.backlog.breakdown.prodNewIssues
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.backlog &&
                                                        data.backlog.breakdown.prodNewIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Total Issue points:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.backlog &&
                                                    fetchAction.data.backlog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.backlog &&
                                                        data.backlog.breakdown &&
                                                        data.backlog.breakdown.issuePoints === null
                                                        ? "N|A"
                                                        : data &&
                                                              data.backlog &&
                                                              data.backlog.breakdown &&
                                                              data.backlog.breakdown.issuePoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Total Prod Issue points:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.backlog &&
                                                    fetchAction.data.backlog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.backlog &&
                                                        data.backlog.breakdown &&
                                                        data.backlog.breakdown.prodIssuePoints ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.backlog &&
                                                              data.backlog.breakdown &&
                                                              data.backlog.breakdown
                                                                  .prodIssuePoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Issue points created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.backlog &&
                                                    fetchAction.data.backlog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.backlog &&
                                                        data.backlog.breakdown &&
                                                        data.backlog.breakdown.newIssuePoints ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.backlog &&
                                                              data.backlog.breakdown &&
                                                              data.backlog.breakdown.issuePoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Prod Issue points created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.backlog &&
                                                    fetchAction.data.backlog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.backlog &&
                                                        data.backlog.breakdown &&
                                                        data.backlog.breakdown
                                                            .prodNewIssuePoints === null
                                                        ? "N|A"
                                                        : data &&
                                                              data.backlog &&
                                                              data.backlog.breakdown &&
                                                              data.backlog.breakdown
                                                                  .prodNewIssuePoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Bugs Backlog Size</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.bugsBacklog &&
                                            data.bugsBacklog.meta &&
                                            data.bugsBacklog.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction &&
                                        fetchAction.data &&
                                        fetchAction.data.bugsBacklog
                                    }
                                >
                                    {executiveValueFormatter(
                                        data && data.bugsBacklog && data.bugsBacklog.total.value,
                                        "custom",
                                        false,
                                        "total issues",
                                        data &&
                                            data.bugsBacklog &&
                                            data.bugsBacklog.stats &&
                                            data.bugsBacklog.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Production Bugs Backlog:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsBacklog &&
                                                    fetchAction.data.bugsBacklog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsBacklog &&
                                                        data.bugsBacklog.breakdown &&
                                                        data.bugsBacklog.breakdown.prodBugIssues ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown
                                                                  .prodBugIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Bug issues created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsBacklog &&
                                                    fetchAction.data.bugsBacklog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsBacklog &&
                                                        data.bugsBacklog.breakdown &&
                                                        data.bugsBacklog.breakdown.newBugIssues ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown
                                                                  .newBugIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Prod Bug issues created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsBacklog &&
                                                    fetchAction.data.bugsBacklog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsBacklog &&
                                                        data.bugsBacklog.breakdown &&
                                                        data.bugsBacklog.breakdown
                                                            .prodNewBugIssues === null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown
                                                                  .prodNewBugIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Total Bug issue points:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsBacklog &&
                                                    fetchAction.data.bugsBacklog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsBacklog &&
                                                        data.bugsBacklog.breakdown &&
                                                        data.bugsBacklog.breakdown.bugPoints ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown.bugPoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Total Prod Bug issue points:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsBacklog &&
                                                    fetchAction.data.bugsBacklog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsBacklog &&
                                                        data.bugsBacklog.breakdown &&
                                                        data.bugsBacklog.breakdown.prodBugPoints ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown
                                                                  .prodBugPoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Bug issue points created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsBacklog &&
                                                    fetchAction.data.bugsBacklog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsBacklog &&
                                                        data.bugsBacklog.breakdown &&
                                                        data.bugsBacklog.breakdown.newBugPoints ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown
                                                                  .newBugPoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Prod Bug issue points created in period:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsBacklog &&
                                                    fetchAction.data.bugsBacklog.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsBacklog &&
                                                        data.bugsBacklog.breakdown &&
                                                        data.bugsBacklog.breakdown
                                                            .prodNewBugPoints === null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown
                                                                  .prodNewBugPoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Bugs Detected</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.bugsDetected &&
                                            data.bugsDetected.meta &&
                                            data.bugsDetected.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction &&
                                        fetchAction.data &&
                                        fetchAction.data.bugsDetected
                                    }
                                >
                                    {executiveValueFormatter(
                                        data &&
                                            data.bugsDetected &&
                                            data.bugsDetected.total &&
                                            data.bugsDetected.total.value,
                                        "custom",
                                        false,
                                        "new bug issues",
                                        data &&
                                            data.bugsDetected &&
                                            data.bugsDetected.stats &&
                                            data.bugsDetected.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>Total Bugs:</MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsDetected &&
                                                    fetchAction.data.bugsDetected.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsDetected &&
                                                        data.bugsDetected.breakdown &&
                                                        data.bugsDetected.breakdown.totalIssues ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsDetected &&
                                                              data.bugsDetected.breakdown &&
                                                              data.bugsDetected.breakdown
                                                                  .totalIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Production Bugs Detected:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsDetected &&
                                                    fetchAction.data.bugsDetected.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsDetected &&
                                                        data.bugsDetected.breakdown &&
                                                        data.bugsDetected.breakdown.prodIssues ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsDetected &&
                                                              data.bugsDetected.breakdown &&
                                                              data.bugsDetected.breakdown
                                                                  .prodIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Total Production Bugs:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsDetected &&
                                                    fetchAction.data.bugsDetected.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsDetected &&
                                                        data.bugsDetected.breakdown &&
                                                        data.bugsDetected.breakdown
                                                            .totalProdIssues === null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsDetected &&
                                                              data.bugsDetected.breakdown &&
                                                              data.bugsDetected.breakdown
                                                                  .totalProdIssues,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Bugs detected issue points:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsDetected &&
                                                    fetchAction.data.bugsDetected.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsDetected &&
                                                        data.bugsDetected.breakdown &&
                                                        data.bugsDetected.breakdown.issuePoints ===
                                                            null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsDetected &&
                                                              data.bugsDetected.breakdown &&
                                                              data.bugsDetected.breakdown
                                                                  .issuePoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Prod Bugs detected issue points:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.bugsDetected &&
                                                    fetchAction.data.bugsDetected.breakdown
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.bugsDetected &&
                                                        data.bugsDetected.breakdown &&
                                                        data.bugsDetected.breakdown
                                                            .prodIssuePoints === null
                                                        ? "N|A"
                                                        : data &&
                                                              data.bugsBacklog &&
                                                              data.bugsBacklog.breakdown &&
                                                              data.bugsBacklog.breakdown
                                                                  .prodIssuePoints,
                                                    undefined,
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Reworked Code Ratio</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.rework &&
                                            data.rework.meta &&
                                            data.rework.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction && fetchAction.data && fetchAction.data.rework
                                    }
                                >
                                    {executiveValueFormatter(
                                        data && data.rework && data.rework.total.value,
                                        "percentile",
                                        false,
                                        "of lines changed that were pre-existing",
                                        data &&
                                            data.rework &&
                                            data.rework.stats &&
                                            data.rework.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Total Commits:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.rework &&
                                                    fetchAction.data.rework.breakdown.commits
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.rework &&
                                                        data.rework.breakdown.commits,
                                                    "number",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>Total lines:</MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.rework &&
                                                    fetchAction.data.rework.breakdown.lines
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.rework &&
                                                        data.rework.breakdown.lines,
                                                    "number",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Reworked lines:
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.rework &&
                                                    fetchAction.data.rework.breakdown.reworkLines
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.rework &&
                                                        data.rework.breakdown.reworkLines,
                                                    "number",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                        </Grid>
                    </>
                )}
            </CustomPaper>
            <CustomPaper>
                {isFlipped && (
                    <>
                        <MetricHeaderText
                            style={{ margin: "0 0 10px 0", fontWeight: 200, opacity: 0.5 }}
                        >
                            Code Credit Cost
                        </MetricHeaderText>
                        <InfoText>
                            These are costs that may or may not have affected current output
                            delivery, but will eventually have to be &quot;paid&quot;. They will
                            affect output via piling backlogs, increasing bugs or hard to maintain
                            code.
                        </InfoText>
                        <Grid container spacing={1}>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            docsPathConstants.codeCreditMaintainabilityDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Maintainability Index
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    The sum of the changes on the code maintainability index of all
                                    repos altered by this group of people in this period.
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.maintainability &&
                                            data.maintainability.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            docsPathConstants.codeCreditSilosDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Code Repository Silos
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Number of code repositories worked by this group of people on
                                    with more than two thirds of the code written by a single
                                    contributor in this group. Indication of knowledge siloing
                                    within the team
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.codeSilos &&
                                            data.codeSilos.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            docsPathConstants.codeCreditBacklogDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Backlog Size
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Amount of current backlog tickets, and the new issues added in
                                    the backlog during the period.
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.backlog &&
                                            data.backlog.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            docsPathConstants.codeCreditBugsBacklogDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Bugs Backlog Size
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Amount of bug backlog tickets, and the new bug issues added in
                                    the backlog during the period.
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.bugsBacklog &&
                                            data.bugsBacklog.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            docsPathConstants.codeCreditBugsDetectedDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Bugs Detected
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Amount of bug tickets assigned or created by this group of
                                    people during the period.
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.bugsDetected &&
                                            data.bugsDetected.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            docsPathConstants.codeCreditReworkDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Reworked Code Ratio
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Ratio of existing code changed over the total amount of changes
                                    done in the code. Indication of how much old code is getting
                                    changed vs new code added without changing the old. Should be
                                    kept in a healthy ratio around the middle zone, depending on the
                                    work done by the team (new project or BAU).
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.rework &&
                                            data.rework.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </>
                )}
            </CustomPaper>
        </ReactCardFlip>
    );
};

export default CodeCost;
